import { FC, useCallback, useContext, useState } from 'react';

import { Theme, Button, Popper, ClickAwayListener } from '@material-ui/core';
import { makeStyles, createStyles } from '@material-ui/styles';
import React from 'react';

import { rescopeDataPath, useDataValue } from '../utils';
import { activityStyles } from './Calendar';
import { LayoutNodeWrapper } from './LayoutNode';
import { DFSPrimitiveType, parseFieldIdentifier } from '../services';
import { FormContext } from '../FormContext';
import { Close } from '@material-ui/icons';
import { getDefaultData } from '../../../../utils/state';

enum EActivityTypes {
  Planting = 'Planting',
  Harvest = 'Harvest',
  ManureApplication = 'Manure Application',
  FertilizerApplication = 'Fertilizer Application',
  Tillage = 'Tillage'
};

const useStyles = makeStyles((theme: Theme) => {

  const { spacing, palette, breakpoints } = theme;

  return createStyles({
    day: {
      display: 'flex',
      width: '15px',
      height: '15px',
      border: '1px solid #666',
      cursor: 'pointer',
      [breakpoints.down('lg')]: {
        width: '11px',
        height: '11px',
      },
      [breakpoints.down('sm')]: {
        width: '5px',
        height: '5px',
      }
    },
    ...activityStyles,
    popover: {
      padding: '12px',
      display: 'block',
      minWidth: '256px',
      maxWidth: '442px',
      backgroundColor: palette.common.white,
      border: '1px solid #ccc'
    },
    popoverHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      marginBottom: '12px'
    },
    dayCounter: {
      fontSize: '7px',
      [breakpoints.down('sm')]: {
        display: 'none'
      },
      cursor: 'pointer'
    },
    actions: {
      display: 'flex',
      justifyContent: 'space-between'
    },
    date: {
      fontWeight: 'bold',
      margin: 0
    },
    createBtn: {
      display: 'flex',
      margin: '0 0 0 auto'
    }
  })
});

type TActivity = {
  "date": string, // YYYY-MM-DD
  "type": EActivityTypes,
}

export const CalendarDay: FC<any> = function(props) {
  const { day, month, year, activities, dataPath, formPath } = props;
  const classes = useStyles({});
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const formContext = useContext(FormContext);
  const { onSetData, onRemoveData, scope, form } = formContext;  
  const [ currentSubform, setCurrentSubform ] = useState(-1);
  const absolutePath = rescopeDataPath(dataPath, scope);
  const subforms = useDataValue(dataPath) as undefined | readonly DFSPrimitiveType[];

  const handleClick = (event: React.MouseEvent<HTMLElement>, day: number, month: number) => {
    setAnchorEl(event.currentTarget);
    const activity = getActivity(day, month)
    if (activity) {
      setCurrentSubform(activity.index);
    }
  };

  const handleDelete = function() {
    onRemoveData('{' + parseFieldIdentifier(absolutePath) + '/' + currentSubform + '}', '');
    setCurrentSubform(-1);
    handleClose();
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  const getActivity = (    
    day: number,
    monthIndex: number,
    type?: EActivityTypes,
  ): { activity: TActivity, index: number}|undefined => {
    let match = undefined;
    (activities || []).forEach((activity: {type: string, date: string}, index: number) => {
      if (!type || (activity && activity.type === type)) {
        const formattedDate = year + '-' + (monthIndex<=9 ? '0'+monthIndex : monthIndex.toString()) + '-' + (day<=9 ? '0'+day : day.toString());
        if (activity.date === formattedDate) {
          match = { activity, index };
        }
      }
    });
    return match;
  }

  const hasActivity = (
    day: number,
    monthIndex: number,
    type?: EActivityTypes,
  ): boolean => {
    return !!getActivity(day, monthIndex, type);
  }

  const zeroPad = (val: number): string => {
    return val<=9 ? '0'+val : val.toString();
  }

  const createItem = useCallback(() => {
    const initialState = getDefaultData(absolutePath, form) as any;
    initialState.date = year + '-' + zeroPad(month) + '-' + zeroPad(day);
    onSetData(dataPath.slice(0, -1).concat('/-}'), scope, initialState);
    Promise.resolve().then(function() {
      return setCurrentSubform(subforms ? subforms.length : 0);
    })
  }, [ subforms, dataPath, form, scope, year ])

  return (
    <div>
      <div
        className = { [
          classes.day,
          hasActivity(day, month, EActivityTypes.Planting) ? classes.hasPlanting : '',
          hasActivity(day, month, EActivityTypes.Harvest) ? classes.hasHarvest : '',
          hasActivity(day, month, EActivityTypes.ManureApplication) ? classes.hasManureApplication : '',
          hasActivity(day, month, EActivityTypes.FertilizerApplication) ? classes.hasFertilizerApplication : '',
          hasActivity(day, month, EActivityTypes.Tillage) ? classes.hasTillage : ''
        ].join(' ') }
        key = { `day-${month}/${day}/${year}` }
        title = { zeroPad(month) + '/' + zeroPad(day) + '/' + year }
        onClick = { (e) => handleClick(e, day, month) }>
          <span className={ classes.dayCounter }>{ day }</span>
      </div>
      { open && 
        <Popper
          id={id}
          open={open}
          anchorEl={ anchorEl }
          placement="right-start"
          disablePortal
        >
          <ClickAwayListener onClickAway={handleClose}>
            <div className = { classes.popover }>
              <div className = { classes.popoverHeader }>
                <p className = { classes.date }>{ zeroPad(month) + '/' + zeroPad(day) + '/' + year } </p>
                <Close onClick={ handleClose }></Close>
              </div>
              { currentSubform === -1 &&
                <div className = { classes.actions }>
                  <Button
                    className = { classes.createBtn }
                    variant = { 'contained' }
                    color = { 'secondary' }
                    onClick = { createItem }>
                      New Activity
                  </Button>
                </div>
              }
              { currentSubform !== -1 &&
                <FormContext.Provider
                value = { Object.assign(
                  Object.assign({}, formContext), {
                    scope: parseFieldIdentifier(absolutePath) + '/' + currentSubform
                  }
                )}>
                  <LayoutNodeWrapper path = { formPath }></LayoutNodeWrapper>
                  <div className = { classes.actions }>
                    <Button
                      variant = { 'contained' }
                      color = { 'secondary' }
                      onClick = { handleDelete }>
                        Delete
                    </Button>
                    <Button
                      variant = { 'contained' }
                      color = { 'secondary' }
                      onClick = { handleClose }>
                        Close
                    </Button>
                  </div>
                </FormContext.Provider>
              }
            </div>
          </ClickAwayListener>
        </Popper>
      } 
    </div>
  )
}